import { Info } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardFooter, CardHeader } from "@/components/ui/card";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import {
  REP_TEXT_FALLBACKS, REP_TEXT_WAIT_MINUTES, repTextChanges, repTextFallbackText, repTextProblem,
  repTextValues, sameRepText, type RepTextValues,
} from "@/lib/campaign";
import { useUpdateCampaign, type CampaignSettingsInput } from "@/lib/queries";
import type { Campaign } from "@/lib/types";

/**
 * Where a text sent to a rep's own number on this campaign goes (ADR-095 in platform). One rule for
 * every such number, beside the rule for a call to one: the rep sees the text at once, and the
 * campaign's inbox shows it too once it has waited unread; or nobody but the rep ever sees it.
 *
 * A text to the campaign's own numbers is not this card's: it lands in the campaign's inbox, which
 * every rep staffed on the campaign reads. The wait is typed in minutes and saved in seconds, and
 * only a field the form changed is sent.
 *
 * The form follows the campaign until somebody edits it. It remembers what it was last filled with
 * (`seeded`), so a rule another admin saved in the meantime replaces an untouched form instead of
 * showing up as this person's unsaved edit, which Save would then put back.
 */
export function CampaignRepTexts({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const toast = useToast();
  const update = useUpdateCampaign();
  const [values, setValues] = React.useState<RepTextValues>(() => repTextValues(campaign));
  // What the form was last filled with from the campaign.
  const seeded = React.useRef(values);
  const [formError, setFormError] = React.useState<string | null>(null);
  const saved = repTextValues(campaign);
  const changes = repTextChanges(values, campaign);
  const dirty = Object.keys(changes).length > 0;
  const problem = repTextProblem(values);
  const shares = values.rep_text_fallback !== "rep_only";

  const seed = (next: RepTextValues) => {
    seeded.current = next;
    setValues(next);
  };

  // A refetch after a save, on focus, or because somebody else saved, re-seeds the form unless this
  // person is mid-edit: the form still showing what it was filled with is nobody's edit.
  React.useEffect(() => {
    if (sameRepText(values, seeded.current)) seed(repTextValues(campaign));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign]);

  const set = <K extends keyof RepTextValues>(key: K, value: RepTextValues[K]) =>
    setValues((now) => ({ ...now, [key]: value }));

  const save = async () => {
    setFormError(null);
    try {
      const next = await update.mutateAsync({ id: campaign.id, ...(changes as CampaignSettingsInput) });
      seed(repTextValues(next));
      toast.success("Texts to rep numbers saved", campaign.name);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the rule for texts to rep numbers.");
    }
  };

  return (
    <Card>
      <CardHeader
        title="Texts to a rep's own number"
        description="A text to a number that names one rep is that rep's. This says whether the campaign's inbox sees it too. It applies to every rep's number here."
      />
      <CardBody className="space-y-4">
        {formError && <Callout tone="danger">{formError}</Callout>}
        <fieldset disabled={!canManage} className="min-w-0 space-y-2">
          <legend className="sr-only">Who sees a text to a rep's own number</legend>
          <div className="grid gap-2 md:grid-cols-2">
            {REP_TEXT_FALLBACKS.map((fallback) => {
              const text = repTextFallbackText(fallback);
              return (
                <label
                  key={fallback}
                  className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border px-3 py-2.5 has-[:checked]:border-accent-8 has-[:checked]:bg-accent-2 has-[:disabled]:cursor-default"
                >
                  <input
                    type="radio"
                    name="rep-text-fallback"
                    className="mt-0.5 accent-accent-9"
                    checked={values.rep_text_fallback === fallback}
                    onChange={() => set("rep_text_fallback", fallback)}
                  />
                  <span className="min-w-0">
                    <span className="block text-xs font-medium text-text">{text.label}</span>
                    <span className="block text-xs text-text-muted">{text.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {shares && (
          <Field
            label="Unread for"
            htmlFor="rep-text-wait"
            hint={`Minutes, up to ${REP_TEXT_WAIT_MINUTES[1]}. 0 shows it in the campaign's inbox at once.`}
          >
            <Input
              type="number"
              inputMode="numeric"
              min={REP_TEXT_WAIT_MINUTES[0]}
              max={REP_TEXT_WAIT_MINUTES[1]}
              className="w-28"
              disabled={!canManage}
              value={Number.isFinite(values.wait_minutes) ? values.wait_minutes : ""}
              onChange={(e) => set("wait_minutes", e.target.valueAsNumber)}
            />
          </Field>
        )}

        <ul className="space-y-1.5">
          <li className="flex items-start gap-2 text-xs text-text-muted">
            <Info className="mt-px size-3.5 shrink-0 text-text-subtle" aria-hidden />
            Once the campaign's inbox shows a conversation, it stays there until the rep opens it.
          </li>
          <li className="flex items-start gap-2 text-xs text-text-muted">
            <Info className="mt-px size-3.5 shrink-0 text-text-subtle" aria-hidden />
            A text to one of the campaign's other numbers goes straight to the campaign's inbox.
          </li>
        </ul>

        {dirty && shares && problem && <Callout tone="warning">{problem}</Callout>}
      </CardBody>
      {canManage && (
        <CardFooter>
          <Button
            variant="outline"
            disabled={!dirty || update.isPending}
            onClick={() => {
              seed(saved);
              setFormError(null);
            }}
          >
            Discard
          </Button>
          <Button
            variant="default"
            loading={update.isPending}
            disabled={!dirty || (shares && problem !== null)}
            onClick={() => void save()}
          >
            Save
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}
